import React, { useState } from "react";
import YouTube from "react-youtube";
import styles from "../styles/home.module.css";
import Features from "./Features";
import Pricing from "./Pricing";

const HeroSection = () => {
  return (
    <section className={`${styles.sectionBase} ${styles.heroSection}`}>
      <div className={styles.contentWrapper}>
        <div className={styles.heroGrid}>
          <div className={styles.heroRow1}>
            <div className={styles.heroImageContainer}>
              <img
                src="/hero-image3.svg"
                alt="Illustration showing Job Assistant automating LinkedIn job applications privately on a user's computer"
                className={styles.heroImage}
                loading="lazy"
              />
            </div>
            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>
                Stop Applying Manually, Start Interviewing Sooner
              </h1>
              <p className={styles.heroSubtitle}>
                Automate your job search with our browser extension. Apply
                smarter, faster, with personalized resumes and seamless
                automation.
              </p>
              <div className={styles.ctaContainer}>
                <a
                  href="/dashboard"
                  className={`${styles.ctaButton} ${styles.ctaButtonPrimary}`}
                >
                  Apply Now
                </a>
                <a
                  href="#how-it-works"
                  className={`${styles.ctaButton} ${styles.ctaButtonSecondary}`}
                >
                  See How It Works
                </a>
              </div>
            </div>
          </div>
          <div className={styles.featureHighlightsGrid}>
            <div className={styles.featureHighlightCard}>
              <span>Runs as a Secure Extension</span>
            </div>
            <div className={styles.featureHighlightCard}>
              <span>Smart Automation</span>
            </div>
            <div className={styles.featureHighlightCard}>
              <span>AI Resume Tailoring</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorks = () => {
  const videoId = "baHTcxCPioM";
  const [playVideo, setPlayVideo] = useState(false);

  const handlePlayClick = () => {
    setPlayVideo(true);
  };

  const handleVideoEnd = () => {
    setPlayVideo(false);
  };

  const playerOptions = {
    height: "100%",
    width: "100%",
    playerVars: {
      autoplay: 1,
      modestbranding: 1,
      rel: 0,
      controls: 1,
      showinfo: 0,
      color: "white",
    },
  };

  return (
    <section
      id="how-it-works"
      className={`${styles.sectionBase} ${styles.howItWorksSection}`}
    >
      <div className={styles.contentWrapper}>
        <div className={styles.howItWorksContent}>
          <h2 className={styles.sectionTitle}>How It Works</h2>
          <div className={styles.stepsGrid}>
            <div className={styles.stepItem}>
              <div className={styles.stepIcon}>
                <i className="fas fa-user"></i>
              </div>
              <div className={styles.stepContent}>
                <h3>1. Set Up Your Profile</h3>
                <p>
                  Create a detailed profile with your skills, experience, and
                  preferences.
                </p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepIcon}>
                <i className="fas fa-edit"></i>
              </div>
              <div className={styles.stepContent}>
                <h3>2. Customize Your Applications</h3>
                <p>
                  Our extension tailors your resume and cover letter to each job
                  automatically.
                </p>
              </div>
            </div>
            <div className={styles.stepItem}>
              <div className={styles.stepIcon}>
                <i className="fas fa-chart-bar"></i>
              </div>
              <div className={styles.stepContent}>
                <h3>3. Track Your Progress</h3>
                <p>
                  Monitor your application status and interview schedule in one
                  place.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          id="how-it-works-video"
          className={`${styles.videoContainer} ${
            playVideo ? styles.videoPlaying : ""
          }`}
          onClick={!playVideo ? handlePlayClick : undefined}
        >
          {playVideo ? (
            <YouTube
              videoId={videoId}
              opts={playerOptions}
              className={styles.videoOverlayIframe}
              onEnd={handleVideoEnd}
            />
          ) : (
            <span className={styles.videoText}>
              See Job Assistant in Action
            </span>
          )}
        </div>
      </div>
    </section>
  );
};

const ResumeCustomization = () => {
  return (
    <section
      className={`${styles.sectionBase} ${styles.resumeCustomizationSection}`}
    >
      <div className={styles.contentWrapper}>
        <div className={styles.featureFocusContent}>
          <div className={styles.featureIcon}>
            <i className="fas fa-edit" aria-hidden="true"></i>
          </div>
          <div className={styles.featureText}>
            <h2 className={styles.sectionTitle}>
              Stand Out with <strong>AI-Powered</strong> Resume Tailoring
            </h2>
            <p
              className={styles.sectionSubtitle}
              style={{ maxWidth: "800px", marginBottom: "1.5rem" }}
            >
              Don't send generic applications. Job Assistant intelligently
              analyzes job descriptions and helps answer supplemental questions
              using your resume, creating highly relevant,{" "}
              <strong>ATS-friendly applications</strong>. Increase your
              interview chances with truly
              <strong>customized job applications</strong>.
            </p>
            <a
              href="/features#resume-ai"
              className={`${styles.ctaButton} ${styles.ctaButtonSecondary}`}
              title="Learn more about AI resume tailoring feature"
            >
              Explore Smart Features
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const SecuritySection = () => {
  return (
    <section className={`${styles.sectionBase} ${styles.securitySection}`}>
      <div className={styles.contentWrapper}>
        <div className={styles.securityContent}>
          <div className={styles.securityIcon}>
            <i className="fas fa-lock" aria-hidden="true"></i>
          </div>
          <div className={styles.securityText}>
            <h3>Your Job Search, Your Data, Your Browser.</h3>
            <p>
              Unlike web-based tools, Job Assistant works entirely as a
              <strong>browser extension</strong>. It runs directly inside your
              browser on your own device no external servers involved. Your
              resume, credentials, and application history never leave your
              computer. Enjoy powerful job search automation with complete
              <strong>privacy, security, and peace of mind</strong>.
            </p>
            <a
              href="/privacy"
              className={`${styles.ctaButton} ${styles.ctaButtonSecondary}`}
              title="Read our commitment to user privacy"
            >
              Our Privacy Commitment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const FinalCTASection = () => {
  return (
    <section className={`${styles.sectionBase} ${styles.finalCtaSection}`}>
      <div className={styles.contentWrapper}>
        <h2 className={styles.sectionTitle}>
          Ready to Accelerate Your <strong>Job Hunt</strong> ?
        </h2>
        <p className={styles.sectionSubtitle}>
          Stop the manual grind. Let our{" "}
          <strong>automated job application tool</strong> find and apply to
          relevant <strong>LinkedIn jobs</strong> while you focus on interviews.
          Get started free!
        </p>
        <a
          href="/dashboard"
          className={`${styles.ctaButton} ${styles.ctaButtonPrimary}`}
          title="Claim your free applications"
        >
          Claim 5 Free Applications{" "}
          <i className="fas fa-arrow-right" aria-hidden="true"></i>
        </a>
      </div>
    </section>
  );
};

const Home = () => {
  return (
    <div className={styles.homeContainer}>
      <HeroSection />
      <HowItWorks />
      <ResumeCustomization />
      <Features />
      <Pricing />
      <SecuritySection />
      <FinalCTASection />
    </div>
  );
};

export default Home;
